import React, { useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore, useChatInputStore, useCompanyStore } from '@/store';
import ModelCard from '@/components/Models/ModelCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import StatusContainer from '@/components/StatusContainer';
import { getExploreGridColumns } from '@/components/Explore/exploreGridColumns';

// Tag slug for video models - same pattern as image models
const VIDEO_TAG_SLUG = 'video-generators';

const createStyles = (colors, isXS, columns) => StyleSheet.create({
  container: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  grid: {
    flexDirection: isXS ? 'column' : 'row',
    flexWrap: isXS ? undefined : 'wrap',
    marginHorizontal: isXS ? 0 : -6,
  },
  gridItem: {
    width: isXS ? '100%' : `${100 / columns}%`,
    paddingHorizontal: isXS ? 0 : 6,
    marginBottom: 12,
  },
  emptyContainer: {
    paddingVertical: 40,
  },
});

const VideoModelsSection = () => {
  const { colors } = useTheme();
  const responsive = useResponsiveness();
  const { isXS } = responsive;
  const columns = getExploreGridColumns(responsive);
  const router = useRouter();
  
  const initializeVideoGeneration = useChatInputStore(state => state.initializeVideoGeneration);
  
  // Get model data from store - same approach as Models page
  const getModelsByTag = useModelStore(state => state.getModelsByTag);
  const fetchModelsByTag = useModelStore(state => state.fetchModelsByTag);
  const isLoadingModelsByTag = useModelStore(state => state.isLoadingModelsByTag);
  const modelsByTag = useModelStore(state => state.modelsByTag);
  const getCompanyById = useCompanyStore(state => state.getCompanyById);
  const fetchModelCompanies = useCompanyStore(state => state.fetchModelCompanies);

  const styles = useMemo(() => createStyles(colors, isXS, columns), [colors, isXS, columns]);

  // Fetch video models on mount - same as Models page does for 'video' tag.
  // Companies hydrate the catalog ModelCard's provider/slug title.
  useEffect(() => {
    fetchModelsByTag(VIDEO_TAG_SLUG);
    fetchModelCompanies?.();
  }, [fetchModelsByTag, fetchModelCompanies]);

  // Get video generation models from the store
  const videoModels = useMemo(() => {
    const models = getModelsByTag(VIDEO_TAG_SLUG) || [];
    // Limit to 9 models so the responsive grid fills cleanly (e.g. 3x3).
    return models.slice(0, 9);
  }, [getModelsByTag, modelsByTag]);

  const handleModelPress = useCallback((modelId) => {
    // Initialize chat with this model selected for video generation
    initializeVideoGeneration('', modelId);
    router.push('/app');
  }, [initializeVideoGeneration, router]);

  if (isLoadingModelsByTag && videoModels.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.grid}>
          {[...Array(4)].map((_, index) => (
            <View key={index} style={styles.gridItem}>
              <ModelCard isLoading={true} />
            </View>
          ))}
        </View>
      </View>
    );
  }

  if (videoModels.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <StatusContainer
            type="empty"
            message="No video generation models available"
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.grid}>
        {videoModels.map((model) => (
          <View key={model.id} style={styles.gridItem}>
            <ModelCard
              model={model}
              company={getCompanyById(model.companyId)}
              onPress={handleModelPress}
            />
          </View>
        ))}
      </View>
    </View>
  );
};

export default React.memo(VideoModelsSection);

